/**
 * File: web/src/main.tsx
 * ======================
 *
 * React application entrypoint (mounting the SPA)
 *
 * Purpose:
 * - Attach the React application to the DOM element with id="root".
 * - Render the <App /> component, which then loads runtime config and sets up routing/providers.
 *
 * When used:
 * - This file is executed first by the browser when the SPA bundle loads.
 * - In a Vite/React build, this is the standard entrypoint that bootstraps React.
 *
 * Learner notes:
 * - This file intentionally does NOT fetch config.json directly.
 *   That responsibility lives in App.tsx so the config state can gate route rendering.
 *
 * Gotcha (React.StrictMode in development):
 * - StrictMode intentionally double-invokes certain lifecycle paths in dev to surface side-effects.
 * - You may see effects run twice during local development (not in production builds).
 * - This is expected; keep startup logic idempotent (e.g., harmless duplicate config fetches).
 */

import React from "react";
import ReactDOM from "react-dom/client";
import { App } from "./App";

/**
 * createRoot() enables React’s modern concurrent rendering behavior.
 * The non-null assertion (!) indicates we expect #root to exist in index.html.
 *
 * If #root is missing, this will throw early (good: fail fast).
 */
ReactDOM.createRoot(document.getElementById("root")!).render(
  /**
   * StrictMode:
   * - Development-only checks for unsafe patterns and unintended side effects.
   * - Helps catch issues early (especially effects that depend on being called once).
   */
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
